import React from "react";
import { Phone, Mail, MapPin } from "lucide-react";

export default function ContactDetails() {
  return (
    <section
      id="contact-details"
      data-testid="contact-details-section"
      className="relative py-24 md:py-28 bg-[var(--gg-deep)] text-white overflow-hidden"
    >
      {/* Beehive crest watermark */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          right: "-80px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "520px",
          height: "560px",
          opacity: 0.07,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 220'><g fill='none' stroke='%23ffffff' stroke-width='3' stroke-linecap='round'><path d='M70 40 Q100 28 130 40'/><path d='M55 60 Q100 44 145 60'/><path d='M45 84 Q100 64 155 84'/><path d='M40 110 Q100 88 160 110'/><path d='M40 138 Q100 116 160 138'/><path d='M45 166 Q100 146 155 166'/><path d='M55 190 L90 190'/><path d='M110 190 L145 190'/><rect x='90' y='190' width='20' height='20' rx='4'/></g><g fill='%23ffffff'><circle cx='168' cy='40' r='3'/><circle cx='178' cy='52' r='2'/><circle cx='185' cy='38' r='2'/></g></svg>\")",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
        }}
      />
      {/* Honeycomb pattern on dark */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='84' height='96' viewBox='0 0 84 96'><g fill='none' stroke='%23ffffff' stroke-width='1.1' stroke-linejoin='round'><polygon points='21,2 42,14 42,38 21,50 0,38 0,14'/><polygon points='63,2 84,14 84,38 63,50 42,38 42,14'/><polygon points='21,50 42,62 42,86 21,98 0,86 0,62'/><polygon points='63,50 84,62 84,86 63,98 42,86 42,62'/></g></svg>\")",
          backgroundSize: "84px 96px",
          backgroundRepeat: "repeat",
          opacity: 0.05,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--gg-secondary)] font-semibold">
            Contact Details
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[54px] mt-4 leading-[1.05] font-semibold text-balance">
            Speak with us,
            <br />
            <em className="not-italic text-[var(--gg-secondary)] font-normal">
              wherever you are.
            </em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PhoneCard
            testid="contact-phone-jm"
            phone="876-449-6400"
            label="Jamaica"
            telHref="tel:+18764496400"
            waHref="https://wa.me/18764496400"
          />
          <PhoneCard
            testid="contact-phone-ca"
            phone="437-347-2020"
            label="Canada"
            telHref="tel:+14373472020"
            waHref="https://wa.me/14373472020"
          />
          <PhoneCard
            testid="contact-phone-us"
            phone="203-550-6378"
            label="United States"
            telHref="tel:+12035506378"
            waHref="https://wa.me/12035506378"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <ContactCard
            testid="contact-email"
            href="mailto:info@caregracefulguidance.com"
            icon={<Mail size={16} />}
            label="Email"
            value="info@caregracefulguidance.com"
          />
          <ContactCard
            testid="contact-address"
            icon={<MapPin size={16} />}
            label="Office"
            value="Ironshore, Montego Bay, Jamaica"
          />
        </div>
      </div>
    </section>
  );
}

function PhoneCard({ testid, phone, label, telHref, waHref }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 hover:border-white/25 transition-colors p-5 h-full"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
        {label}
      </div>
      <div className="font-serif text-lg mt-1 truncate">{phone}</div>
      <div className="flex gap-2 mt-3">
        <a
          href={telHref}
          data-testid={`${testid}-call`}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          aria-label={`Call ${phone}`}
        >
          <Phone size={14} />
        </a>
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testid}-whatsapp`}
          className="w-9 h-9 rounded-full bg-[#25D366]/90 hover:bg-[#25D366] flex items-center justify-center transition-colors"
          aria-label={`WhatsApp ${phone}`}
        >
          {/* WhatsApp icon */}
          <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" className="text-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}

function ContactCard({ testid, href, target, icon, label, value }) {
  const inner = (
    <div className="flex items-center gap-4 rounded-2xl bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 hover:border-white/25 transition-colors p-5 h-full">
      <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
          {label}
        </div>
        <div className="font-serif text-lg mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
  if (!href) {
    return (
      <div data-testid={testid} className="block">
        {inner}
      </div>
    );
  }
  return (
    <a
      data-testid={testid}
      href={href}
      target={target}
      rel={target === "_blank" ? "noreferrer" : undefined}
      className="block group"
    >
      {inner}
    </a>
  );
}
